import { nullOnNotFound, ResourceClient, type HttpClient } from "@dl/web-core-js/api";
import type {
  ArtworkCoreDTO,
  ArtworkDTO,
  ArtworkImageDTO,
  ArtworkSearchDTO,
  CatalogDTO,
  ClientDTO,
  ClientMailingAddressDTO,
  ClientNoteDTO,
  CollectionDTO,
  CountryDTO,
  DecorDTO,
  DecorType,
  DisplayCollectionDTO,
  DraftOrderDTO,
  FeedType,
  GlobalDpiDTO,
  JoliscreenImageDTO,
  KpiClientDTO,
  KpiStoreDTO,
  KpiTotalSalesDTO,
  LabelSheetType,
  LazyArtworkDTO,
  LegacyProductionJobDTO,
  MimeImageType,
  NewDraftOrderRequest,
  OrderDTO,
  OrderDispatchRequest,
  OrderNoteDTO,
  PrintSettingsDTO,
  PrintSettingsLineDTO,
  ProductCategoryDTO,
  ProductClientPriceLineDTO,
  ProductDTO,
  ProductEntityDTO,
  ProductStorePriceLineDTO,
  ProductionCenterDTO,
  ProductionJobCoreDTO,
  ProductionJobDTO,
  ProductionJobLineDTO,
  PublisherDTO,
  PublisherMetadataTemplateDTO,
  PublisherMetadataTemplateFieldMappingDTO,
  RegionDTO,
  Ratio,
  ShopifyCollection,
  ShopifyCollectionDTO,
  ShopifyProduct,
  ShopifyPublication,
  SkuMatchDTO,
  SkuSalesDTO,
  StoreDTO,
  Tuple2,
  AspectRatioStrategy,
  ShopifyCatalog,
} from "./types";

/**
 * TypeScript twins of `client-logic`'s REST clients.
 *
 * One class per Java client, same method names, same paths — so a change on
 * either side is a one-to-one diff. Everything generic (find/findById/create/
 * update/remove/batch) comes from `ResourceClient`, exactly as the Java clients
 * inherit it from `AControllerClient`.
 */

// ── artwork ───────────────────────────────────────────────────────────────

export class ArtworkClient extends ResourceClient<ArtworkDTO> {
  constructor(http: HttpClient) {
    super(http, "/artwork");
  }

  getByPublisher(publisherId: number) {
    return this.http.get<ArtworkDTO[]>(`/artwork/publisher/${publisherId}`);
  }

  getByPublisherCode(code: string) {
    return nullOnNotFound(this.http.get<ArtworkDTO | null>(`/artwork/publisher/code/${encodeURIComponent(code)}`));
  }

  getByCode(code: string) {
    return nullOnNotFound(this.http.get<ArtworkDTO | null>(`/artwork/code/${encodeURIComponent(code)}`));
  }

  getCodeMap() {
    return this.http.get<Record<string, string>>("/artwork/codes");
  }

  /** Fire-and-forget: the server recomputes max print size in the background. */
  async updateMaxPrintDimensions(ids: number[]) {
    await this.http.put<void>("/artwork/update-max-print-size", ids, { responseType: "void" });
  }

  async regenerateProducts(id: number) {
    await this.http.post<void>("/artwork/regenerate/products", null, {
      responseType: "void",
      query: { artwork_id: id },
    });
  }

  /** Returns the Shopify sync failures; an empty list means everything landed. */
  updateCode(id: number, code: string) {
    return this.http.post<string[]>(`/artwork/${id}/code`, null, { query: { code } });
  }

  resyncShopify(id: number) {
    return this.http.post<string[]>(`/artwork/${id}/resync-shopify`, null);
  }

  /**
   * TERMINATED, and deleted from every Shopify store each artwork is listed on.
   * Synchronous: returns the failures, an empty list when everything landed.
   */
  terminate(ids: number[]) {
    return this.http.post<string[]>("/artwork/terminate", ids);
  }

  /**
   * Uploads one image. The endpoint takes its metadata as query params and the
   * bytes as a raw octet-stream body — there is no multipart form anywhere in
   * this estate.
   */
  async add(params: {
    catalog_id: number;
    publisher_id: number;
    publisher_code: string;
    code: string;
    aspect_ratio_strategy: AspectRatioStrategy;
    dpi?: number | null;
    ratio?: Ratio | null;
    file: File;
  }) {
    const { file, dpi, ratio, ...rest } = params;
    await this.http.request<void>("/artwork/add", {
      method: "POST",
      raw: file,
      responseType: "void",
      query: {
        ...rest,
        ...(dpi !== null && dpi !== undefined ? { dpi } : {}),
        ...(ratio ? { ratio } : {}),
      },
    });
  }

  /** Rows matched on our code (the default), or on a publisher's own codes among that publisher's artworks. */
  importMetadata(file: File, match: "CODE" | "PUBLISHER_CODE" = "CODE", publisherId?: number) {
    return this.http.request<string[]>("/artwork/import/metadata", {
      method: "POST",
      raw: file,
      query: match === "PUBLISHER_CODE" ? { match, publisher_id: publisherId } : { match },
    });
  }

  documentMetadata(ids: number[]) {
    return this.http.job("/artwork/document/metadata", { body: ids });
  }

  /** The products of these artworks in these categories: type, SKU, UPC, sizes. */
  documentProducts(artworkIds: number[], categoryIds: number[]) {
    const payload: Tuple2<number[], number[]> = { first: artworkIds, second: categoryIds };
    return this.http.job("/artwork/document/products", { body: payload });
  }

  documentFeed(feedType: FeedType, artworkIds: number[], categoryIds: number[]) {
    const payload: Tuple2<number[], number[]> = { first: artworkIds, second: categoryIds };
    return this.http.job("/artwork/document/feed", {
      body: payload,
      query: { feed_type: feedType },
    });
  }
}

export class ArtworkCoreClient extends ResourceClient<ArtworkCoreDTO> {
  constructor(http: HttpClient) {
    super(http, "/artwork/core");
  }
}

export class ArtworkSearchClient extends ResourceClient<ArtworkSearchDTO> {
  constructor(http: HttpClient) {
    super(http, "/artwork/search");
  }
}

export class LazyArtworkClient extends ResourceClient<LazyArtworkDTO> {
  constructor(http: HttpClient) {
    super(http, "/artwork/lazy");
  }

  getByCode(code: string) {
    return nullOnNotFound(this.http.get<LazyArtworkDTO | null>(`/artwork/lazy/code/${encodeURIComponent(code)}`));
  }

  getByPublisherCode(code: string) {
    return nullOnNotFound(this.http.get<LazyArtworkDTO | null>(
      `/artwork/lazy/publisher/code/${encodeURIComponent(code)}`,
    ));
  }
}

export class ArtworkImageClient extends ResourceClient<ArtworkImageDTO> {
  constructor(http: HttpClient) {
    super(http, "/artwork/image");
  }

  async syncDimensions() {
    await this.http.post<void>("/artwork/image/sync-dimensions", null, { responseType: "void" });
  }

  async uploadCorrectedImage(artworkId: number, file: File) {
    await this.http.request<void>("/artwork/image/corrected", {
      method: "POST",
      raw: file,
      responseType: "void",
      query: { artwork_id: artworkId },
    });
  }

  async deleteCorrectedImage(artworkId: number) {
    await this.http.request<void>("/artwork/image/corrected", {
      method: "DELETE",
      responseType: "void",
      query: { artwork_id: artworkId },
    });
  }

  getThumbnailMap(ids: number[]) {
    return this.http.post<Record<string, ArtworkImageDTO>>("/artwork/image/thumbnail", ids);
  }
}

// ── reference data ────────────────────────────────────────────────────────

export class CatalogClient extends ResourceClient<CatalogDTO> {
  constructor(http: HttpClient) {
    super(http, "/catalog");
  }
}

export class CollectionClient extends ResourceClient<CollectionDTO> {
  constructor(http: HttpClient) {
    super(http, "/collection");
  }
}

export class PublisherClient extends ResourceClient<PublisherDTO> {
  constructor(http: HttpClient) {
    super(http, "/publisher");
  }
}

export class PublisherMetadataTemplateClient extends ResourceClient<PublisherMetadataTemplateDTO> {
  constructor(http: HttpClient) {
    super(http, "/publisher/metadata/template");
  }

  findByPublisherId(publisherId: number) {
    return nullOnNotFound(this.http.get<PublisherMetadataTemplateDTO | null>(
      `/publisher/metadata/template/publisher/${publisherId}`,
    ));
  }

  importMetadata(publisherId: number, filename: string, file: File) {
    return this.http.request<string[]>("/publisher/metadata/template/import", {
      method: "POST",
      raw: file,
      query: { publisher_id: publisherId, filename },
    });
  }
}

export class PublisherMetadataTemplateFieldMappingClient extends ResourceClient<PublisherMetadataTemplateFieldMappingDTO> {
  constructor(http: HttpClient) {
    super(http, "/publisher/metadata/template/field-mapping");
  }
}

export class CountryClient extends ResourceClient<CountryDTO> {
  constructor(http: HttpClient) {
    super(http, "/country");
  }
}

export class RegionClient extends ResourceClient<RegionDTO> {
  constructor(http: HttpClient) {
    super(http, "/region");
  }
}

export class StoreClient extends ResourceClient<StoreDTO> {
  constructor(http: HttpClient) {
    super(http, "/store");
  }

  getDefault() {
    return nullOnNotFound(this.http.get<StoreDTO | null>("/store/default"));
  }
}

export class ClientClient extends ResourceClient<ClientDTO> {
  constructor(http: HttpClient) {
    super(http, "/client");
  }

  async syncAll() {
    await this.http.post<void>("/client/sync", null, { responseType: "void" });
  }

  sync(id: number) {
    return this.http.post<ClientDTO>(`/client/sync/${id}`, null);
  }
}

export class ClientMailingAddressClient extends ResourceClient<ClientMailingAddressDTO> {
  constructor(http: HttpClient) {
    super(http, "/client/address");
  }
}

export class ClientNoteClient extends ResourceClient<ClientNoteDTO> {
  constructor(http: HttpClient) {
    super(http, "/client/note");
  }
}

export class GlobalDpiClient {
  constructor(private readonly http: HttpClient) {}

  get() {
    return nullOnNotFound(this.http.get<GlobalDpiDTO | null>("/global-dpi"));
  }
}

// ── product ───────────────────────────────────────────────────────────────

export class ProductClient extends ResourceClient<ProductDTO> {
  constructor(http: HttpClient) {
    super(http, "/product");
  }

  documentPricing(open: boolean) {
    return this.http.job("/product/document/pricing", { method: "GET", query: { open } });
  }

  importPricing(file: File) {
    return this.http.request<string[]>("/product/import/pricing", { method: "POST", raw: file });
  }
}

export class ProductCategoryClient extends ResourceClient<ProductCategoryDTO> {
  constructor(http: HttpClient) {
    super(http, "/product/category");
  }

  /** Only categories that still have open (distributable) products. */
  getWithOpenProducts() {
    return this.http.get<ProductCategoryDTO[]>("/product/category/open");
  }
}

export class ProductEntityClient {
  constructor(
    private readonly http: HttpClient,
    private readonly path = "/product/entity",
  ) {}

  getByArtwork(artworkId: number) {
    return this.http.get<ProductEntityDTO[]>(`${this.path}/artwork/${artworkId}`);
  }

  getBySku(sku: string) {
    return nullOnNotFound(this.http.get<ProductEntityDTO | null>(`${this.path}/sku/${encodeURIComponent(sku)}`));
  }
}

/** Same shape, but the server eagerly loads each product's images. */
export class ProductEntityWithImagesClient extends ProductEntityClient {
  constructor(http: HttpClient) {
    super(http, "/product/entity-with-images");
  }
}

export class ProductStorePriceLineClient extends ResourceClient<ProductStorePriceLineDTO> {
  constructor(http: HttpClient) {
    super(http, "/product/store-price-line");
  }

  /** Keyed by product id. */
  getByStore(storeId: number) {
    return this.http.get<Record<string, ProductStorePriceLineDTO>>(
      `/product/store-price-line/store/${storeId}`,
    );
  }

  /** Keyed by store id. */
  getByProduct(productId: number) {
    return this.http.get<Record<string, ProductStorePriceLineDTO>>(
      `/product/store-price-line/product/${productId}`,
    );
  }
}

export class ProductClientPriceLineClient extends ResourceClient<ProductClientPriceLineDTO> {
  constructor(http: HttpClient) {
    super(http, "/product/client-price-line");
  }

  /** Keyed by product id. */
  getByClient(clientId: number) {
    return this.http.get<Record<string, ProductClientPriceLineDTO>>(
      `/product/client-price-line/client/${clientId}`,
    );
  }

  /** Keyed by client id. */
  getByProduct(productId: number) {
    return this.http.get<Record<string, ProductClientPriceLineDTO>>(
      `/product/client-price-line/product/${productId}`,
    );
  }
}

// ── collections ───────────────────────────────────────────────────────────

export class DisplayCollectionClient extends ResourceClient<DisplayCollectionDTO> {
  constructor(http: HttpClient) {
    super(http, "/display/collection");
  }

  getByUUID(uuid: string) {
    return nullOnNotFound(this.http.get<DisplayCollectionDTO | null>(`/display/collection/uuid/${uuid}`));
  }

  async addArtworkImages(collectionId: number, artworkIds: number[], type: string) {
    await this.http.post<void>(`/display/collection/${collectionId}/artworks`, artworkIds, {
      responseType: "void",
      query: { type },
    });
  }
}

// ── decor ─────────────────────────────────────────────────────────────────

export class DecorClient extends ResourceClient<DecorDTO> {
  constructor(http: HttpClient) {
    super(http, "/decor");
  }

  getByProduct(productId: number) {
    return this.http.get<DecorDTO[]>(`/decor/by-product/${productId}`);
  }

  /** The simple create flow: params on the query string, background as bytes. */
  createSimple(params: {
    product_id: number;
    pos_x: number;
    pos_y: number;
    width: number;
    height: number;
    type: DecorType;
    mime: MimeImageType;
    background: File;
  }) {
    const { background, ...query } = params;
    return this.http.request<DecorDTO>("/decor/create", {
      method: "POST",
      raw: background,
      query,
    });
  }

  async archive(id: number) {
    await this.http.put<void>(`/decor/${id}/archive`, null, { responseType: "void" });
  }
}

// ── joliscreen ────────────────────────────────────────────────────────────

export class JoliscreenClient extends ResourceClient<JoliscreenImageDTO> {
  constructor(http: HttpClient) {
    super(http, "/joliscreen");
  }

  async add(artworkIds: number[]) {
    await this.http.post<void>("/joliscreen/add", artworkIds, { responseType: "void" });
  }

  async sync() {
    await this.http.put<void>("/joliscreen/update", null, { responseType: "void" });
  }

  async updateS3() {
    await this.http.put<void>("/joliscreen/update-s3", null, { responseType: "void" });
  }

  async activate(ids: number[]) {
    await this.http.put<void>("/joliscreen/activate", ids, { responseType: "void" });
  }

  async suspend(ids: number[]) {
    await this.http.put<void>("/joliscreen/suspend", ids, { responseType: "void" });
  }
}

// ── kpi ───────────────────────────────────────────────────────────────────

export class KpiClientClient extends ResourceClient<KpiClientDTO> {
  constructor(http: HttpClient) {
    super(http, "/kpi/client");
  }

  getAllLatest() {
    return this.http.get<KpiClientDTO[]>("/kpi/client/latest");
  }

  getTotalSales() {
    return this.http.get<KpiTotalSalesDTO>("/kpi/client/total");
  }
}

export class KpiStoreClient extends ResourceClient<KpiStoreDTO> {
  constructor(http: HttpClient) {
    super(http, "/kpi/store");
  }

  getAllLatest() {
    return this.http.get<KpiStoreDTO[]>("/kpi/store/latest");
  }

  getTotalSales() {
    return this.http.get<KpiTotalSalesDTO>("/kpi/store/total");
  }
}

// ── orders ────────────────────────────────────────────────────────────────

export class OrderClient extends ResourceClient<OrderDTO> {
  constructor(http: HttpClient) {
    super(http, "/order");
  }

  getByDateRange(from: string, to: string) {
    return this.http.get<OrderDTO[]>("/order/date", { query: { from, to } });
  }

  getSkuByDateRange(from: string, to: string) {
    return this.http.get<SkuSalesDTO[]>("/order/sku", { query: { from, to } });
  }
}

export class DraftOrderClient extends ResourceClient<DraftOrderDTO> {
  constructor(http: HttpClient) {
    super(http, "/draft/order");
  }
}

export class OrderNoteClient extends ResourceClient<OrderNoteDTO> {
  constructor(http: HttpClient) {
    super(http, "/order/note");
  }

  getByOrder(orderType: string, orderId: number) {
    return this.http.get<OrderNoteDTO[]>(
      `/order/note/${encodeURIComponent(orderType)}/${orderId}`,
    );
  }
}

export class ShopifyOrderClient {
  constructor(private readonly http: HttpClient) {}

  async syncAsync(storeId: number, lastUpdateDate?: string) {
    const path = lastUpdateDate
      ? `/shopify/order/sync/async/${encodeURIComponent(lastUpdateDate)}`
      : "/shopify/order/sync/async";
    await this.http.post<void>(path, null, { responseType: "void", query: { store_id: storeId } });
  }

  async draftSyncAsync(storeId: number) {
    await this.http.post<void>("/shopify/order/draft/sync/async", null, {
      responseType: "void",
      query: { store_id: storeId },
    });
  }

  async resync(storeId: number) {
    await this.http.post<void>("/shopify/order/resync", null, {
      responseType: "void",
      query: { store_id: storeId },
    });
  }

  draftCreate(storeId: number, request: NewDraftOrderRequest) {
    return this.http.post<DraftOrderDTO>("/shopify/order/draft/create", request, {
      query: { store_id: storeId },
    });
  }

  draftComplete(storeId: number, id: string) {
    return this.http.put<OrderDTO>("/shopify/order/draft/complete", null, {
      query: { store_id: storeId, id },
    });
  }

  draftDelete(storeId: number, id: string) {
    return this.http.put<boolean>("/shopify/order/draft/delete", null, {
      query: { store_id: storeId, id },
    });
  }

  importOrders(storeId: number, clientId: number, file: File) {
    return this.http.request<string[]>("/shopify/order/draft/import", {
      method: "POST",
      raw: file,
      query: { store_id: storeId, client_id: clientId },
    });
  }
}

// ── shopify products / collections ────────────────────────────────────────

export class ShopifyProductClient {
  constructor(private readonly http: HttpClient) {}

  async sync(storeId: number) {
    await this.http.post<void>("/shopify/product/sync", null, {
      responseType: "void",
      query: { store_id: storeId },
    });
  }

  getPublications(storeId: number) {
    return this.http.get<ShopifyPublication[]>("/shopify/product/publications", {
      query: { store_id: storeId },
    });
  }

  get(storeId: number, artworkId: number) {
    return nullOnNotFound(this.http.get<ShopifyProduct | null>("/shopify/product", {
      query: { store_id: storeId, artwork_id: artworkId },
    }));
  }

  async updateVariants(
    storeId: number,
    catalog: ShopifyCatalog,
    artworkIds: number[],
    categoryIds: number[],
  ) {
    const payload: Tuple2<number[], number[]> = { first: artworkIds, second: categoryIds };
    await this.http.put<void>("/shopify/product/update/variants", payload, {
      responseType: "void",
      query: { store_id: storeId, catalog },
    });
  }

  async updateVariantsPrice(storeId: number, artworkIds: number[]) {
    await this.http.put<void>("/shopify/product/update/variants/price", artworkIds, {
      responseType: "void",
      query: { store_id: storeId },
    });
  }

  async publish(storeId: number, artworkIds: number[]) {
    await this.http.put<void>("/shopify/product/publish", artworkIds, {
      responseType: "void",
      query: { store_id: storeId },
    });
  }

  async unpublish(storeId: number, artworkIds: number[]) {
    await this.http.put<void>("/shopify/product/unpublish", artworkIds, {
      responseType: "void",
      query: { store_id: storeId },
    });
  }

  async create(
    storeId: number,
    catalog: ShopifyCatalog,
    artworkIds: number[],
    categoryIds: number[],
  ) {
    const payload: Tuple2<number[], number[]> = { first: artworkIds, second: categoryIds };
    await this.http.post<void>("/shopify/product/create", payload, {
      responseType: "void",
      query: { store_id: storeId, catalog },
    });
  }

  async remove(storeId: number, artworkIds: number[]) {
    await this.http.request<void>("/shopify/product/remove", {
      method: "DELETE",
      body: artworkIds,
      responseType: "void",
      query: { store_id: storeId },
    });
  }
}

export class ShopifyCollectionClient extends ResourceClient<ShopifyCollectionDTO> {
  constructor(http: HttpClient) {
    super(http, "/shopify/collection");
  }

  /** Every persisted mapping row, across every store. */
  collectionsAll() {
    return this.http.get<ShopifyCollectionDTO[]>("/shopify/collection/all");
  }

  /** Live Shopify collections keyed by store id. */
  getCollectionsAll() {
    return this.http.get<Record<string, ShopifyCollection[]>>("/shopify/collection/collections/all");
  }

  getCollections(storeId: number) {
    return this.http.get<ShopifyCollection[]>("/shopify/collection/collections", {
      query: { store_id: storeId },
    });
  }

  getCollection(storeId: number, collectionId: string) {
    return nullOnNotFound(this.http.get<ShopifyCollection | null>("/shopify/collection/collection", {
      query: { store_id: storeId, collection_id: collectionId },
    }));
  }

  getArtworks(storeId: number, collectionId: number) {
    return this.http.get<LazyArtworkDTO[]>("/shopify/collection/artworks", {
      query: { store_id: storeId, id: collectionId },
    });
  }

  async sync(storeId: number) {
    await this.http.post<void>("/shopify/collection/sync", null, {
      responseType: "void",
      query: { store_id: storeId },
    });
  }

  reorder(storeId: number, collectionId: number, artworkIds: number[]) {
    return this.http.put<boolean>("/shopify/collection/reorder", artworkIds, {
      query: { store_id: storeId, id: collectionId },
    });
  }

  updateTitle(storeId: number, collectionId: number, title: string) {
    return this.http.put<boolean>("/shopify/collection/update/title", null, {
      query: { store_id: storeId, id: collectionId, title },
    });
  }

  /** Creates the collection on Shopify itself and returns the persisted mapping. */
  createRemote(storeId: number, title: string) {
    return this.http.post<ShopifyCollectionDTO>("/shopify/collection/create", null, {
      query: { store_id: storeId, title },
    });
  }

  /** Persists a mapping row for a collection that already exists on Shopify. */
  createMapping(storeId: number, entity: Partial<ShopifyCollectionDTO>) {
    return this.http.post<ShopifyCollectionDTO>("/shopify/collection", entity, {
      query: { store_id: storeId },
    });
  }

  /** Drops the local mapping row only; the Shopify collection stays. */
  async removeMapping(storeId: number, id: number) {
    await this.http.request<void>(`/shopify/collection/${id}`, {
      method: "DELETE",
      responseType: "void",
      query: { store_id: storeId },
    });
  }

  /** Deletes the collection on Shopify. */
  async deleteCollection(storeId: number, id: number) {
    await this.http.request<void>("/shopify/collection/delete/collection", {
      method: "DELETE",
      responseType: "void",
      query: { store_id: storeId, id },
    });
  }
}

// ── production ────────────────────────────────────────────────────────────

export class ProductionCenterClient extends ResourceClient<ProductionCenterDTO> {
  constructor(http: HttpClient) {
    super(http, "/production/center");
  }

  getAvailable(countryCode: string, productId: number) {
    return this.http.get<ProductionCenterDTO[]>("/production/center/available", {
      query: { country_code: countryCode, product_id: productId },
    });
  }

  getAvailableByCountry(countryCode: string) {
    return this.http.get<ProductionCenterDTO[]>("/production/center/available/country", {
      query: { country_code: countryCode },
    });
  }
}

export class ProductionJobClient extends ResourceClient<ProductionJobDTO> {
  constructor(http: HttpClient) {
    super(http, "/production/job");
  }

  getByOrderHeaderId(orderHeaderId: number) {
    return this.http.get<ProductionJobDTO[]>(`/production/job/order/${orderHeaderId}`);
  }

  getByOrderHeaderIds(ids: number[]) {
    return this.http.post<ProductionJobDTO[]>("/production/job/order", ids);
  }

  dispatch(requests: OrderDispatchRequest[]) {
    return this.http.post<ProductionJobDTO[]>("/production/job/dispatch", requests);
  }

  printed(ids: number[]) {
    return this.http.post<boolean>("/production/job/printed", ids);
  }

  shipped(ids: number[]) {
    return this.http.post<boolean>("/production/job/shipped", ids);
  }

  getJobCard(id: number, labelSheetType: LabelSheetType) {
    return this.http.job(`/production/job/jobcard/${id}`, { body: labelSheetType });
  }
}

/** The lightweight job read model — no order or centre joined in. */
export class ProductionJobCoreClient extends ResourceClient<ProductionJobCoreDTO> {
  constructor(http: HttpClient) {
    super(http, "/production/job/core");
  }
}

export class ProductionJobLineClient extends ResourceClient<ProductionJobLineDTO> {
  constructor(http: HttpClient) {
    super(http, "/production/job/line");
  }

  getByProductionJobId(jobId: number) {
    return this.http.get<ProductionJobLineDTO[]>(`/production/job/line/job/${jobId}`);
  }
}

export class LegacyProductionJobClient extends ResourceClient<LegacyProductionJobDTO> {
  constructor(http: HttpClient) {
    super(http, "/legacy/production/job");
  }

  async retry(ids: number[]) {
    await this.http.post<void>("/legacy/production/job/retry", ids, { responseType: "void" });
  }

  async cancel(ids: number[]) {
    await this.http.post<void>("/legacy/production/job/cancel", ids, { responseType: "void" });
  }
}

export class SkuMatchClient extends ResourceClient<SkuMatchDTO> {
  constructor(http: HttpClient) {
    super(http, "/production/sku-match");
  }

  getBySku(sku: string) {
    return nullOnNotFound(this.http.get<SkuMatchDTO | null>(`/production/sku-match/sku/${encodeURIComponent(sku)}`));
  }
}

export class PrintSettingsClient extends ResourceClient<PrintSettingsDTO> {
  constructor(http: HttpClient) {
    super(http, "/print-settings");
  }
}

export class PrintSettingsLineClient extends ResourceClient<PrintSettingsLineDTO> {
  constructor(http: HttpClient) {
    super(http, "/print-settings/line");
  }

  getByPrintSettings(printSettingsId: number) {
    return this.http.get<PrintSettingsLineDTO[]>(
      `/print-settings/line/print-settings/${printSettingsId}`,
    );
  }

  getByProduct(productId: number) {
    return this.http.get<PrintSettingsLineDTO[]>(`/print-settings/line/product/${productId}`);
  }

  getByProductionCenter(centerId: number) {
    return this.http.get<PrintSettingsLineDTO[]>(
      `/print-settings/line/production-center/${centerId}`,
    );
  }
}
